// Story 10.1/10.5 — Sidebar de navegación. Colapsable; estado en `ui-store`.
// Story 10.5 filtra los links por `capability` del user con `useCan`.
// Debajo de `md` es un panel que se abre desde el botón de menú del TopBar.
import { Link, useParams, useRouterState } from '@tanstack/react-router';
import {
  BookOpen,
  Bot,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileEdit,
  LayoutDashboard,
  LineChart,
  MessageSquare,
  Settings,
  Users,
  X,
} from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useCan } from '@/hooks/use-can';
import type { Capability } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useMobileNavStore, useUIStore } from '@/stores/ui-store';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Si true, requiere `botId` en la ruta actual. */
  scoped?: boolean;
  /** Capability requerida para que el link aparezca. */
  capability: Capability;
}

const TOP_LINKS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, capability: 'view_dashboard' },
  { to: '/organizations', label: 'Organizaciones', icon: Building2, capability: 'view_dashboard' },
  { to: '/admin/users', label: 'Usuarios', icon: Users, capability: 'manage_users' },
  { to: '/audit', label: 'Auditoría', icon: ClipboardList, capability: 'manage_members' },
  { to: '/docs', label: 'Docs', icon: BookOpen, capability: 'view_dashboard' },
];

const BOT_LINKS: NavItem[] = [
  { to: '/bots/$botId', label: 'Resumen', icon: Bot, scoped: true, capability: 'view_bot' },
  {
    to: '/bots/$botId/editor',
    label: 'Editor',
    icon: FileEdit,
    scoped: true,
    capability: 'edit_bot',
  },
  {
    to: '/bots/$botId/conversations',
    label: 'Conversaciones',
    icon: MessageSquare,
    scoped: true,
    capability: 'view_conversations',
  },
  {
    to: '/bots/$botId/analytics',
    label: 'Analytics',
    icon: LineChart,
    scoped: true,
    capability: 'view_analytics_basic',
  },
  {
    to: '/bots/$botId/settings',
    label: 'Settings',
    icon: Settings,
    scoped: true,
    capability: 'view_settings',
  },
];

export function Sidebar(): React.ReactElement {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggle = useUIStore((s) => s.toggleSidebar);
  const mobileOpen = useMobileNavStore((s) => s.open);
  const setMobileNav = useMobileNavStore((s) => s.setOpen);
  // Match params ofrece `botId` cuando estamos dentro de `/bots/:botId/*`.
  const params = useParams({ strict: false }) as { botId?: string };
  const botId = params.botId;
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // En pantallas angostas el menú es un panel encima del contenido: se
  // cierra al navegar, con Esc o tocando fuera.
  useEffect(() => {
    if (pathname) setMobileNav(false);
  }, [pathname, setMobileNav]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNav(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, setMobileNav]);

  return (
    <>
      <aside
        data-testid="sidebar"
        className={cn(
          'hidden flex-col border-r border-border bg-card transition-[width] duration-200 md:flex',
          collapsed ? 'w-16' : 'w-60',
        )}
        aria-label="Navegación principal"
      >
        <div className="flex h-14 items-center justify-between px-4">
          {!collapsed && <span className="text-sm font-semibold tracking-wide">Revel Bots</span>}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
        <SidebarNav collapsed={collapsed} botId={botId} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Cerrar menú"
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileNav(false)}
          />
          <aside
            id="mobile-nav"
            className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col border-r border-border bg-card shadow-xl"
            aria-label="Navegación principal"
          >
            <div className="flex h-14 items-center justify-between px-4">
              <span className="text-sm font-semibold tracking-wide">Revel Bots</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileNav(false)}
                aria-label="Cerrar menú"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <SidebarNav collapsed={false} botId={botId} onNavigate={() => setMobileNav(false)} />
          </aside>
        </div>
      )}
    </>
  );
}

function SidebarNav(props: {
  collapsed: boolean;
  botId?: string;
  onNavigate?: () => void;
}): React.ReactElement {
  const { collapsed, botId, onNavigate } = props;
  return (
    <nav className="flex-1 overflow-y-auto p-2" aria-label="Navegación">
      <ul className="space-y-1">
        {TOP_LINKS.map((item) => (
          <SidebarLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
        {botId != null && (
          <>
            <li className="mt-4 px-2 text-xs font-medium uppercase text-muted-foreground">
              {!collapsed && 'Bot'}
            </li>
            {BOT_LINKS.map((item) => (
              <SidebarLink
                key={item.to}
                item={item}
                collapsed={collapsed}
                params={{ botId }}
                onNavigate={onNavigate}
              />
            ))}
          </>
        )}
      </ul>
    </nav>
  );
}

interface SidebarLinkProps {
  item: NavItem;
  collapsed: boolean;
  params?: Record<string, string>;
  onNavigate?: () => void;
}

function SidebarLink({
  item,
  collapsed,
  params,
  onNavigate,
}: SidebarLinkProps): React.ReactElement | null {
  // Story 10.5 — ocultar el link si el user no tiene la capability. Story
  // 16.2: los links del bot deciden con las capabilities de ESE bot.
  const scopedBotId = item.scoped && params?.botId ? Number(params.botId) : undefined;
  const allowed = useCan(item.capability, scopedBotId);
  if (!allowed) return null;
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={item.to}
        params={params}
        // biome-ignore lint/suspicious/noExplicitAny: TanStack params typing relaxed for the shared component.
        {...({} as any)}
        className={cn(
          'flex items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'aria-[current=page]:bg-accent aria-[current=page]:text-accent-foreground',
        )}
        activeProps={{ 'aria-current': 'page' }}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        {!collapsed && <span>{item.label}</span>}
      </Link>
    </li>
  );
}
